import { readAnswer } from "@/features/adapters/model/answers";
import { MAPPING_RESOURCE_KINDS, findMappingById, getGraphNodes } from "@/features/adapters/model/helpers";
import { isResourceApplicable } from "./applicability";
import { inspectionIdentity, notRequiredResult, resourceOwnership } from "./results";
import type {
  ContactEntityResourceDefinition,
  ResourceApplyResult,
  ResourceDriver,
  ResourceDriverContext,
  ResourceInspection,
} from "./types";

interface Relationship {
  id?: string;
  name?: string;
  textSingular?: string;
  containment?: boolean;
  contactEntity?: boolean;
  contactId?: string;
}

/**
 * Where the release stands on the contact entity: the entity is already it, another entity (or global) is, it can be
 * marked with the chosen contact ID, or it can't be marked yet and the message says why. The entity's relationship is
 * included whenever it exists, so it can be opened for editing.
 */
export type ContactEntityPlan =
  | { state: "marked"; message: string; relationship: Relationship & { id: string } }
  | { state: "otherContact"; message: string; relationship: Relationship & { id: string } }
  | {
      state: "unmarked";
      message: string;
      relationship: Relationship & { id: string };
      contactId: string;
      label: string;
      column: string;
    }
  | { state: "blocked"; message: string; relationship?: Relationship & { id: string } };

export type ContactEntityState = ContactEntityPlan["state"];

/** What the inspection carries for the block: the plan's state and the relationship to edit. */
export interface ContactEntityValue {
  plan: ContactEntityState;
  relationshipId?: string;
}

const text = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** The entity's singular name without a leading "the" (Data Model names often read "the employee"). */
const relationshipLabel = (relationship: Relationship): string =>
  (text(relationship.textSingular) ?? text(relationship.name) ?? "this entity").replace(/^the\s+/i, "");

/** Entity labels are usually lower case; a message that starts with one starts with a capital. */
const sentence = (message: string): string => `${message.charAt(0).toUpperCase()}${message.slice(1)}`;

/**
 * Plans marking the entity named by the adapter's answer as the contact entity. The contact ID is the attribute chosen
 * for the declared column of the adapter's saved mapping, which must be one of the entity's own attributes (the
 * relationships endpoint rejects anything else). One contact entity per release, so another one is left alone.
 * Messages are one short status line; the block's buttons say what can be done about it.
 */
export const planContactEntity = (
  context: Pick<ResourceDriverContext, "release" | "answers" | "resolveResourceId">,
  definition: ContactEntityResourceDefinition,
): ContactEntityPlan => {
  const { entityAnswerKey, contactId } = definition.resource;
  const release = context.release as typeof context.release & {
    relationships?: Relationship[];
    meta?: { contact?: string | null };
  };
  const name = text(readAnswer(context.answers, entityAnswerKey));
  if (!name) return { state: "blocked", message: "Choose the entity first." };
  const relationships = release.relationships ?? [];
  const found = relationships.find((candidate) => candidate.containment !== false && text(candidate.name) === name);
  if (!found?.id) return { state: "blocked", message: `The "${name}" entity isn't in this project.` };
  const relationship = found as Relationship & { id: string };
  const label = relationshipLabel(relationship);

  if (release.meta?.contact === "global") {
    return { state: "otherContact", message: `The global entity is already the contact entity, so ${label} can't be too.`, relationship };
  }
  const current = relationships.find((candidate) => candidate.contactEntity === true);
  if (current && current.id !== relationship.id) {
    return {
      state: "otherContact",
      message: sentence(`${relationshipLabel(current)} is already the contact entity, so ${label} can't be too.`),
      relationship,
    };
  }
  if (current) return { state: "marked", message: sentence(`${label} is the contact entity.`), relationship };

  const notYet = sentence(`${label} isn't the contact entity, so imported ${label} details are nested instead of on the contact.`);
  const blocked = (reason?: string): ContactEntityPlan =>
    ({ state: "blocked", message: reason ? `${notYet} ${reason}` : notYet, relationship });

  // Until the mapping's column has an attribute, the entity is marked by editing it.
  const mapping = findMappingById(release, context.resolveResourceId(MAPPING_RESOURCE_KINDS, contactId.mappingKey));
  if (!mapping) return blocked();
  const columns = isRecord(mapping) && Array.isArray(mapping.columns) ? (mapping.columns as unknown[]) : [];
  const column = columns.find((candidate) => isRecord(candidate) && candidate.id === contactId.columnId) as
    | Record<string, unknown>
    | undefined;
  const header = text(column?.header) ?? contactId.columnId;
  const field = text(column?.field);
  if (!field) return blocked();
  // The release can come back from a subresource write without its rule graph; the server checks the attribute then.
  const nodes = getGraphNodes(release);
  const node = nodes.find((candidate) => candidate.id === field);
  if (nodes.length > 0 && node?.entity !== name) {
    return blocked(node
      ? `The ${header} column's attribute isn't one of ${label}'s.`
      : `The ${header} column's attribute isn't in this project.`);
  }
  return { state: "unmarked", message: notYet, relationship, contactId: field, label, column: header };
};

const inspectContactEntity = (
  context: ResourceDriverContext,
  definition: ContactEntityResourceDefinition,
): ResourceInspection<ContactEntityValue> => {
  const plan = planContactEntity(context, definition);
  const marked = plan.state === "marked";
  // Another contact entity is the project's choice: it's flagged, but isn't something left to do.
  const complete = marked || plan.state === "otherContact";
  return {
    ...inspectionIdentity(definition),
    ownership: resourceOwnership(marked, plan.state === "otherContact"),
    exists: marked,
    complete,
    state: marked ? "ready" : plan.state === "otherContact" ? "conflict" : "missing",
    message: plan.message,
    resourceId: marked ? plan.relationship.id : undefined,
    value: { plan: plan.state, relationshipId: plan.relationship?.id },
    inputs: [{
      id: definition.resource.key,
      title: definition.resource.name,
      instructions: definition.resource.description,
      complete,
      todo: complete ? [] : [plan.message],
    }],
    completeCount: complete ? 1 : 0,
    action:
      definition.applicable && plan.state === "unmarked"
        ? { kind: "apply", label: `Make ${plan.label} the contact entity`, stepId: definition.stepId }
        : undefined,
  };
};

export const contactEntityResourceDriver: ResourceDriver<ContactEntityResourceDefinition, "contactEntity"> = {
  kind: "contactEntity",
  setup: "automatic",
  describe: (declaration) => declaration.resource.name,
  define: (context, owner, declaration) => [{
    key: `${owner.stepId}:${declaration.resource.key}`,
    stepId: owner.stepId,
    definitionKey: declaration.resource.key,
    kind: "contactEntity",
    label: declaration.resource.name,
    applicable: isResourceApplicable(context.answers, owner, declaration.resource),
    resource: declaration.resource,
  }],
  inspect: (context, definition) => inspectContactEntity(context, definition),
  // Never fails the step: what can't be marked yet (or is already another entity) is reported instead.
  ensure: async (context, definition): Promise<ResourceApplyResult> => {
    if (!definition.applicable) return notRequiredResult(definition);
    const plan = planContactEntity(context, definition);
    if (plan.state !== "unmarked") return { message: plan.message };
    const release = await context.operations.updateRelationship(plan.relationship.id, {
      contactEntity: true,
      contactId: plan.contactId,
    });
    return {
      release,
      message: sentence(`Made ${plan.label} the contact entity, identified by the ${plan.column} column's attribute.`),
    };
  },
};
